<template>
  <div :class="['external-chat', { 'is-inline': isInline }]" :style="viewportStyle">
    <div class="topline">
      <button type="button" class="back" @click="$router.push({ name: 'communications' })">← Коммуникации</button>
      <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loading" @click="load(true)">Обновить</button>
    </div>

    <div v-if="loading && !contact" class="empty">Загрузка…</div>
    <div v-else-if="!contact" class="empty">Диалог не найден.</div>
    <template v-else>
      <section :class="['identity-notice', noticeTone]">
        <div class="notice-title">{{ identityNotice.title }}</div>
        <div>{{ identityNotice.text }}</div>
        <div v-if="identityNotice.privacyReminder" class="privacy-reminder">
          {{ identityNotice.privacyReminder }}
        </div>
      </section>

      <div class="layout">
        <main class="dialog-card">
          <div class="dialog-head">
            <div>
              <h1>{{ contact.displayName || 'Внешний контакт' }}</h1>
              <div class="sub">{{ dialogSourceLabel }}</div>
            </div>
            <span :class="['identity-pill', noticeTone]">{{ identityLabel(contact.identityStatus) }}</span>
          </div>

          <div ref="history" class="history">
            <div v-if="history.length === 0" class="empty inner">Сообщений пока нет.</div>
            <div v-for="item in history" :key="item.id" :class="messageRowClass(item)">
              <div v-if="item.isSystemEvent" class="system-event">
                {{ item.message }}
                <span>{{ formatDate(item.createdAt) }}</span>
              </div>
              <div v-else class="bubble">
                <div class="bubble-head">
                  <b>{{ item.displayName || senderLabel(item) }}</b>
                  <span>{{ item.channelLabel || 'Talk-Me' }}</span>
                </div>
                <div class="message-text">{{ primaryMessageText(item) || 'Вложение' }}</div>
                <details v-if="quotedMessageText(item)" class="email-quote">
                  <summary>Предыдущая переписка</summary>
                  <div class="message-text quoted">{{ quotedMessageText(item) }}</div>
                </details>
                <div
                  v-if="item.attaches && item.attaches.length"
                  class="external-attachments">
                  <a
                    v-for="attach in item.attaches"
                    :key="attach.id"
                    :href="attach.url"
                    target="_blank"
                    rel="noopener">
                    <img :src="attach.url" :alt="attach.name || 'Вложение Talk-Me'">
                  </a>
                </div>
                <div class="bubble-time">{{ formatDate(item.createdAt) }}</div>
              </div>
            </div>
          </div>

          <div class="composer">
            <textarea
              v-model.trim="draft"
              rows="3"
              :maxlength="channel.maxLength || 4000"
              :placeholder="composerPlaceholder"
              @keydown.ctrl.enter.prevent="sendDraft"
              @keydown.meta.enter.prevent="sendDraft"
            ></textarea>
            <details v-if="isEmailChannel && channel.emailSignature" class="email-signature-preview">
              <summary>Подпись Email добавится автоматически</summary>
              <pre>{{ channel.emailSignature }}</pre>
            </details>
            <div class="composer-actions">
              <div class="draft-note">{{ channel.reason }}</div>
              <div class="composer-buttons">
                <button
                  v-if="channel.enabled"
                  class="btn btn-primary btn-sm"
                  :disabled="!draft || sendLoading"
                  @click="sendDraft"
                >
                  {{ sendLoading ? 'Отправляем…' : sendButtonLabel }}
                </button>
                <button
                  v-else
                  class="btn btn-primary btn-sm"
                  :disabled="!draft"
                  @click="copyText(copyDraft, copySuccessText)"
                >
                  Скопировать ответ
                </button>
              </div>
            </div>
          </div>
        </main>

        <aside class="side">
          <section class="side-card reply-assistant-card">
            <div class="reply-card-title">
              <h3>Быстрые ответы</h3>
              <small>{{ externalChannelDisplay }}</small>
            </div>
            <div class="reply-section">
              <div class="reply-section-title">Быстрые</div>
              <button v-for="item in externalQuickReplies" :key="`quick-${item.key}`" type="button" class="sidebar-reply" @click="insertExternalReply(item.message)">
                <b>{{ item.title }}</b><span>{{ item.message }}</span>
              </button>
            </div>
            <details class="reply-templates">
              <summary>Все шаблоны · {{ externalTemplates.length }}</summary>
              <div class="reply-template-list">
                <button v-for="item in externalTemplates" :key="`template-${item.key}`" type="button" class="sidebar-reply template" @click="insertExternalReply(item.message)">
                  <b>{{ item.title }}</b><span>{{ item.message }}</span>
                </button>
              </div>
            </details>
          </section>

          <section class="side-card">
            <h3>Контакт</h3>
            <div class="kv"><span>Источник</span><b>{{ externalChannelDisplay }}</b></div>
            <div class="kv"><span>Client ID</span><b class="mono">{{ contact.providerContactId }}</b></div>
            <div class="kv"><span>Телефон из внешнего канала</span><b>{{ contact.declaredPhone || '—' }}</b></div>
            <div class="kv"><span>E-mail из внешнего канала</span><b>{{ contact.declaredEmail || '—' }}</b></div>
            <div class="kv"><span>Первое входящее</span><b>{{ formatDate(contact.firstInboundAt) }}</b></div>
            <div v-if="contact.candidate" class="candidate">
              <span>
                {{ contact.historicalArchive ? 'Историческое сопоставление' : 'Точное совпадение данных' }}
              </span>
              <b>{{ contact.candidate.name }} · User #{{ contact.candidate.userId }}</b>
              <small>
                {{ contact.historicalArchive
                  ? 'Это историческая подсказка оператору, а не подтверждённая личность.'
                  : 'Это кандидат, а не подтверждённая личность.' }}
              </small>
            </div>
          </section>

          <section v-if="verification && verification.available" class="side-card verify-card">
            <h3>{{ verification.title }}</h3>
            <p>{{ verification.body }}</p>
            <button class="btn btn-outline-primary btn-sm" :disabled="verificationLoading" @click="createVerification">
              {{ verificationLoading ? 'Создаём…' : verification.button }}
            </button>
            <div v-if="verificationUrl" class="verification-result">
              <b>Ссылка готова</b>
              <p>Скопируйте и отправьте её собеседнику через Talk-Me.</p>
              <input :value="verificationUrl" readonly>
              <button class="btn btn-primary btn-sm" @click="copyText(verificationUrl, 'Ссылка скопирована')">Скопировать ссылку</button>
            </div>
          </section>

          <section class="side-card channel-card">
            <h3>{{ channel.label }}</h3>
            <p>{{ channel.reason }}</p>
          </section>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import {
  supportExternal,
  supportExternalRead,
  createSupportExternalVerification,
  sendSupportExternalMessage,
} from '@/api/support';
import { iwalyNotify } from '@/notify';

export default {
  props: {
    // Inline /communications passes the selected external contact here.
    // Standalone /communications/external/:id continues to use the route param.
    externalContactId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      verificationLoading: false,
      sendLoading: false,
      contact: null,
      identityNotice: {},
      history: [],
      channel: {},
      verification: null,
      verificationUrl: '',
      draft: '',
      externalQuickReplies: [
        { key: 'external_hello', title: 'Приветствие', message: 'Добрый день! Вас приветствует команда iWALY. Чем можем помочь?' },
        { key: 'external_clarify', title: 'Уточнить обращение', message: 'Пожалуйста, уточните ваш вопрос или номер заказа, чтобы мы могли быстрее помочь.' },
        { key: 'external_checking', title: 'Проверяем', message: 'Спасибо! Информацию получили. Проверяем и вернёмся с ответом в этом чате.' },
      ],
      externalTemplates: [
        { key: 'external_hello', title: 'Приветствие', message: 'Добрый день! Вас приветствует команда iWALY. Чем можем помочь?' },
        { key: 'external_clarify', title: 'Уточнить обращение', message: 'Пожалуйста, уточните ваш вопрос или номер заказа, чтобы мы могли быстрее помочь.' },
        { key: 'external_checking', title: 'Проверяем', message: 'Спасибо! Информацию получили. Проверяем и вернёмся с ответом в этом чате.' },
        { key: 'external_received', title: 'Информация получена', message: 'Спасибо, информацию получили. Передали коллегам и вернёмся с ответом здесь.' },
        { key: 'external_finish', title: 'Завершение', message: 'Спасибо за обращение! Если появятся дополнительные вопросы, напишите нам в этом чате.' },
      ],
      pollTimer: null,
      viewportHeight: null,
    };
  },
  computed: {
    externalId() {
      const raw = this.externalContactId !== null && this.externalContactId !== ''
        ? this.externalContactId
        : this.$route.params.id;
      const id = Number(raw);
      return Number.isInteger(id) && id > 0 ? id : null;
    },
    isEmailChannel() { return this.channel && this.channel.key === 'email'; },
    externalChannelDisplay() {
      const label = (this.channel && this.channel.label) || 'Talk-Me';
      return label.includes('Talk-Me') ? label : `${label} · Talk-Me`;
    },
    dialogSourceLabel() {
      if (!this.contact) return this.externalChannelDisplay;
      return this.contact.historicalArchive
        ? `Архив · ${this.externalChannelDisplay}`
        : `${this.externalChannelDisplay} · внешний источник`;
    },
    composerPlaceholder() {
      if (this.isEmailChannel) return 'Ответ по Email';
      return this.channel.enabled
        ? `Ответить в ${this.externalChannelDisplay}`
        : `Ответ для ${this.externalChannelDisplay}`;
    },
    sendButtonLabel() {
      return this.isEmailChannel ? 'Отправить Email' : 'Отправить';
    },
    copyDraft() {
      return this.isEmailChannel ? this.composeEmailDraft(this.draft) : this.draft;
    },
    copySuccessText() {
      return this.isEmailChannel ? 'Ответ скопирован вместе с подписью' : 'Ответ скопирован';
    },
    noticeTone() { return (this.identityNotice && this.identityNotice.tone) || 'neutral'; },
    isInline() { return this.externalContactId !== null && this.externalContactId !== ''; },
    viewportStyle() {
      return this.viewportHeight ? { '--external-viewport-height': `${this.viewportHeight}px` } : {};
    },
  },
  watch: {
    externalId(next, previous) {
      if (next && next !== previous) {
        this.load(true);
        this.updateViewportHeight();
      }
    },
  },
  mounted() {
    this.updateViewportHeight();
    window.addEventListener('resize', this.updateViewportHeight);
    this.load(true);
    this.pollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.load(false);
    }, 8000);
  },
  beforeDestroy() {
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    window.removeEventListener('resize', this.updateViewportHeight);
  },
  methods: {
    updateViewportHeight() {
      this.$nextTick(() => {
        if (!this.$el || typeof window === 'undefined') return;
        const { top } = this.$el.getBoundingClientRect();
        const bottomGap = this.isInline ? 12 : 20;
        const available = Math.floor(window.innerHeight - top - bottomGap);
        this.viewportHeight = Math.max(360, available);
      });
    },
    async load(showSpinner = false) {
      if (!this.externalId) return;
      if (showSpinner) this.loading = true;
      const historyEl = this.$refs.history;
      const historyScrollTop = historyEl ? historyEl.scrollTop : 0;
      const historyNearBottom = !historyEl || historyEl.scrollHeight - historyEl.scrollTop - historyEl.clientHeight < 24;
      const pageScrollX = typeof window !== 'undefined' ? window.scrollX : 0;
      const pageScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
      try {
        const response = await supportExternal(this.externalId);
        const data = response.data || {};
        this.contact = data.contact || null;
        this.identityNotice = data.identityNotice || {};
        this.history = data.history || [];
        this.channel = data.channel || {};
        if (Array.isArray(data.quickReplies) && data.quickReplies.length) {
          this.externalQuickReplies = data.quickReplies;
        }
        if (Array.isArray(data.templates) && data.templates.length) {
          this.externalTemplates = data.templates;
        }
        this.verification = data.verification || null;
        if (this.history.some((item) => item.senderType === 'user' && !item.adminReadAt && item.origin !== 'imported')) {
          await supportExternalRead(this.externalId);
        }
        this.$nextTick(() => {
          if (showSpinner) this.updateViewportHeight();
          const el = this.$refs.history;
          if (el) {
            if (showSpinner || historyNearBottom) el.scrollTop = el.scrollHeight;
            else el.scrollTop = historyScrollTop;
          }
          if (!showSpinner && typeof window !== 'undefined' && (window.scrollX !== pageScrollX || window.scrollY !== pageScrollY)) {
            window.scrollTo(pageScrollX, pageScrollY);
          }
        });
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось загрузить внешний диалог' });
      } finally {
        if (showSpinner) this.loading = false;
      }
    },
    async sendDraft() {
      if (!this.channel.enabled || !this.draft || this.sendLoading) return;
      const message = this.draft;
      this.sendLoading = true;
      try {
        await sendSupportExternalMessage(this.externalId, message);
        this.draft = '';
        iwalyNotify.success({ text: this.isEmailChannel ? 'Email отправлен' : 'Ответ отправлен в Talk-Me' });
        await this.load(false);
      } catch (error) {
        const data = error && error.response && error.response.data ? error.response.data : {};
        iwalyNotify.error({ text: data.description || (this.isEmailChannel ? 'Не удалось отправить Email' : 'Не удалось отправить ответ в Talk-Me') });
        await this.load(false);
      } finally {
        this.sendLoading = false;
      }
    },
    insertExternalReply(text) {
      const value = String(text || '');
      if (!value) return;
      this.draft = value;
    },
    stripEmailShortSignoff(text) {
      const value = String(text || '').trim();
      const offset = Math.max(0, value.length - 700);
      const head = value.slice(0, offset);
      const tail = value.slice(offset).replace(
        /(?:^|\n{1,3})С уважением[ \t]*,?[ \t]*\n+[ \t]*Команда iWALY[^\n]*(?:\n[ \t]*www\.iwaly\.ru)?/iu,
        '\n',
      );
      return `${head}${tail}`.trim();
    },
    composeEmailDraft(text) {
      const body = this.stripEmailShortSignoff(text);
      const signature = String((this.channel && this.channel.emailSignature) || '').trim();
      if (!signature) return body;
      const tail = body.toLocaleLowerCase('ru').slice(-3500);
      const alreadyFull = tail.includes('confidentiality notice:')
        && tail.includes('info@iwaly.ru')
        && tail.includes('izumrudnaya');
      if (alreadyFull) return body;
      return body ? `${body}\n\n${signature}` : signature;
    },
    async createVerification() {
      this.verificationLoading = true;
      try {
        const response = await createSupportExternalVerification(this.externalId);
        this.verificationUrl = (response.data && response.data.url) || '';
        if (this.verificationUrl) iwalyNotify.success({ text: 'Ссылка подтверждения создана' });
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось создать ссылку подтверждения' });
      } finally {
        this.verificationLoading = false;
      }
    },
    async copyText(value, successText) {
      if (!value) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const input = document.createElement('textarea');
          input.value = value;
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        iwalyNotify.success({ text: successText });
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось скопировать' });
      }
    },
    identityLabel(status) {
      return {
        verified: 'Личность подтверждена',
        unverified_match: 'Совпадение есть · личность не подтверждена',
        conflict: 'Данные противоречат друг другу',
        ambiguous: 'Неоднозначное совпадение',
        guest: 'Внешний контакт',
      }[status] || 'Внешний контакт';
    },
    splitEmailMessage(item) {
      const text = String((item && item.message) || '');
      if (!item || item.channel !== 'email') return { primary: text, quoted: '' };
      const markers = [
        '\nСообщение послано в ответ на:',
        '\nОтправлено в ответ на:',
        '\n-----Original Message-----',
      ];
      let index = -1;
      markers.forEach((marker) => {
        const candidate = text.indexOf(marker);
        if (candidate >= 0 && (index < 0 || candidate < index)) index = candidate;
      });
      if (index < 0) return { primary: text, quoted: '' };
      return {
        primary: text.slice(0, index).trim(),
        quoted: text.slice(index + 1).trim(),
      };
    },
    primaryMessageText(item) { return this.splitEmailMessage(item).primary; },
    quotedMessageText(item) { return this.splitEmailMessage(item).quoted; },
    messageRowClass(item) {
      if (item.isSystemEvent) return ['message-row', 'system'];
      return ['message-row', item.senderType === 'admin' ? 'operator' : 'client'];
    },
    senderLabel(item) { return item.senderType === 'admin' ? 'Оператор' : 'Собеседник'; },
    formatDate(value) {
      const date = moment(value);
      return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '—';
    },
  },
};
</script>

<style scoped>
.external-chat {
  box-sizing:border-box;
  width:100%;
  height:var(--external-viewport-height, auto);
  max-height:var(--external-viewport-height, none);
  min-height:0;
  padding:22px;
  max-width:1480px;
  margin:0 auto;
  display:flex;
  flex-direction:column;
  overflow:hidden
}
.external-chat.is-inline {
  padding:12px 14px 10px
}
.topline {
  flex:0 0 auto;
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:12px
}
.back {
  border:0;
  background:none;
  color:#547087;
  padding:4px 0;
  cursor:pointer
}
.identity-notice {
  flex:0 0 auto;
  border:1px solid #e3e6eb;
  border-left-width:4px;
  border-radius:10px;
  padding:13px 15px;
  margin-bottom:14px;
  font-size:13px;
  line-height:1.45;
  background:#fff
}
.identity-notice.warning {
  border-left-color:#d8a241;
  background:#fffdf7
}
.identity-notice.danger {
  border-left-color:#c76c68;
  background:#fffafa
}
.identity-notice.success {
  border-left-color:#4f9b70;
  background:#f8fcfa
}
.notice-title {
  font-weight:800;
  margin-bottom:4px
}
.privacy-reminder {
  margin-top:7px;
  font-weight:700
}
.layout {
  flex:1 1 auto;
  min-height:0;
  overflow:hidden;
  display:grid;
  grid-template-columns:minmax(0,1fr) 330px;
  gap:15px;
  align-items:stretch
}
.dialog-card,.side-card {
  background:#fff;
  border:1px solid #e1e5eb;
  border-radius:12px
}
.dialog-card {
  min-width:0;
  min-height:0;
  height:100%;
  overflow:hidden;
  display:flex;
  flex-direction:column
}
.dialog-head {
  flex:0 0 auto;
  padding:16px 18px;
  border-bottom:1px solid #eef0f3;
  display:flex;
  justify-content:space-between;
  gap:12px;
  align-items:center
}
.dialog-head h1 {
  margin:0;
  font-size:19px
}
.sub {
  font-size:11px;
  color:#8a929e;
  margin-top:4px
}
.identity-pill {
  font-size:10px;
  border-radius:12px;
  padding:5px 8px;
  background:#f0f2f5;
  color:#687385;
  text-align:center
}
.identity-pill.warning {
  background:#fff2d9;
  color:#7e5a20
}
.identity-pill.danger {
  background:#fdebea;
  color:#8a403b
}
.identity-pill.success {
  background:#eaf7ef;
  color:#2f7650
}
.history {
  flex:1 1 auto;
  height:auto;
  min-height:0;
  overflow-y:auto;
  overscroll-behavior:contain;
  padding:18px;
  background:#f8f9fb
}
.message-row {
  display:flex;
  margin:7px 0
}
.message-row.operator {
  justify-content:flex-end
}
.message-row.client {
  justify-content:flex-start
}
.bubble {
  max-width:76%;
  background:#fff;
  border:1px solid #e2e6eb;
  border-radius:12px;
  padding:9px 11px
}
.operator .bubble {
  background:#edf7f2;
  border-color:#d5e9dd
}
.bubble-head {
  display:flex;
  gap:6px;
  align-items:center;
  font-size:10px;
  color:#7b8493;
  margin-bottom:5px
}
.message-text {
  white-space:pre-wrap;
  overflow-wrap:anywhere;
  font-size:13px
}
.email-quote { margin-top:8px; border-top:1px solid #edf0f3; padding-top:7px }
.email-quote summary { cursor:pointer; color:#667487; font-size:10px; font-weight:700 }
.email-quote .quoted { margin-top:7px; max-height:260px; overflow:auto; font-size:11px; color:#687385 }
.bubble-time {
  font-size:9px;
  color:#9aa1ab;
  text-align:right;
  margin-top:5px
}
.system {
  justify-content:center
}
.system-event {
  font-size:10px;
  color:#808a98;
  background:#eef1f4;
  border-radius:12px;
  padding:5px 9px;
  max-width:80%;
  text-align:center
}
.system-event span {
  margin-left:6px;
  color:#a0a6ae
}
.composer {
  flex:0 0 auto;
  padding:10px 12px 11px;
  border-top:1px solid #eef0f3;
  background:#fff
}
.composer textarea {
  width:100%;
  resize:vertical;
  border:1px solid #d7dce3;
  border-radius:9px;
  padding:10px;
  font-size:13px;
  min-height:72px;
  max-height:150px
}
.email-signature-preview { margin-top:8px; border:1px solid #e1e6ec; border-radius:8px; padding:8px 10px; background:#fafbfc }
.email-signature-preview summary { cursor:pointer; color:#3b765e; font-size:10px; font-weight:700 }
.email-signature-preview pre {
  margin:8px 0 0;
  max-height:180px;
  overflow:auto;
  white-space:pre-wrap;
  word-break:break-word;
  font-family:inherit;
  font-size:10px;
  line-height:1.4;
  color:#687385
}
.composer-actions {
  display:flex;
  justify-content:space-between;
  gap:10px;
  align-items:center;
  margin-top:8px
}

.composer-buttons {
  display:flex;
  gap:8px;
  flex:0 0 auto
}
.draft-note {
  font-size:10px;
  color:#89919c
}
.side {
  min-height:0;
  height:100%;
  overflow-y:auto;
  overscroll-behavior:contain;
  padding-right:2px;
  display:flex;
  flex-direction:column;
  gap:12px
}
.side-card {
  padding:15px
}
.side-card h3 {
  font-size:14px;
  margin:0 0 11px
}
.side-card p {
  font-size:12px;
  color:#657183;
  line-height:1.48
}
.kv {
  display:flex;
  justify-content:space-between;
  gap:12px;
  padding:6px 0;
  border-bottom:1px solid #f0f2f4;
  font-size:11px
}
.kv span {
  color:#87909c
}
.kv b {
  text-align:right;
  overflow-wrap:anywhere
}
.mono {
  font-family:monospace
}
.candidate {
  margin-top:12px;
  padding:10px;
  background:#fff7e7;
  border:1px solid #f0ddb9;
  border-radius:9px;
  display:flex;
  flex-direction:column;
  gap:3px;
  font-size:11px
}
.candidate span,.candidate small {
  color:#806631
}
.verify-card {
  border-color:#ead8b8;
  background:#fffdf8
}
.verification-result {
  margin-top:12px;
  padding-top:12px;
  border-top:1px solid #eee1ca
}
.verification-result input {
  width:100%;
  font-size:10px;
  padding:8px;
  border:1px solid #d9dce1;
  border-radius:6px;
  margin:5px 0 8px
}
.channel-card {
  background:#fafbfc
}
.reply-assistant-card { padding:12px }
.reply-card-title { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:10px }
.reply-card-title h3 { margin:0 }
.reply-card-title small { color:#8a929e; font-size:9px; font-weight:600 }
.reply-section + .reply-templates { margin-top:9px }
.reply-section-title { margin-bottom:5px; color:#7b8493; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.03em }
.sidebar-reply { display:block; width:100%; margin:0 0 5px; padding:7px 8px; border:1px solid #e0e4ea; border-radius:8px; background:#fff; text-align:left; cursor:pointer }
.sidebar-reply:hover { border-color:#8fbca8; background:#f5fbf8 }
.sidebar-reply b,.sidebar-reply span { display:block }
.sidebar-reply b { color:#394655; font-size:10px; line-height:14px }
.sidebar-reply span { margin-top:2px; color:#7b8493; font-size:9px; line-height:13px }
.reply-templates { border-top:1px solid #edf0f3; padding-top:8px }
.reply-templates > summary { cursor:pointer; color:#3b765e; font-size:10px; font-weight:700 }
.reply-template-list { margin-top:6px }
.empty {
  padding:30px;
  color:#7c8693
}
.empty.inner {
  text-align:center
}
.external-attachments {
  display:flex;
  flex-wrap:wrap;
  gap:7px;
  margin-top:8px;
}
.external-attachments a {
  display:block;
  width:96px;
  height:96px;
  overflow:hidden;
  border:1px solid #e1e6ec;
  border-radius:8px;
  background:#f5f7f8;
}
.external-attachments img {
  width:100%;
  height:100%;
  object-fit:cover;
}


@media(max-width:900px) {
  .external-chat {
    height:auto !important;
    max-height:none !important;
    min-height:0;
    overflow:visible;
    padding:10px
  }
  .layout {
    overflow:visible;
    grid-template-columns:1fr
  }
  .history {
    flex:none;
    height:52vh;
    min-height:340px
  }
  .side {
    order:-1;
    height:auto;
    overflow:visible
  }
  .side-card {
    padding:12px
  }
  .bubble {
    max-width:88%
  }
  .composer-actions {
    align-items:flex-end;
    flex-direction:column
  }
}
</style>
